import { useState, useEffect, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import useCalls from './useCalls';
import useAuth from '../auth/useAuth';
import { Agent } from '../../../types/features/Agent';
import { alertSuccess, alertError } from '../../../helpers/alertError';
import { formatPhoneNumber } from '../../../utils/formatPhonenumber';
import usePageVisibility from '../../core/usePageVisibility';
import useCallNotifications from './useCallNotifications';

interface UseSoftphoneReturn {
  twilioDevice: Device | null;
  deviceStatus: 'deconnected' | 'connecting' | 'registered' | 'error';
  activeCall: any;
  callState: 'idle' | 'ringing' | 'in-call';
  outboundNumber: string;
  isMuted: boolean;
  showSoftphonePanel: boolean;
  activeCallRef: React.MutableRefObject<any>;
  setOutboundNumber: (number: string) => void;
  setShowSoftphonePanel: (show: boolean) => void;
  setIsMuted: (muted: boolean) => void;
  handleMakeCall: (phoneNumber: string) => Promise<void>;
  acceptCall: () => void;
  hangUpCall: () => void;
  toggleMute: () => void;
}

export default function useSoftphone(): UseSoftphoneReturn {
  const { getTwilioToken } = useCalls();
  const { user } = useAuth();
  const { showNotification, closeNotifications } = useCallNotifications();
  const { isVisible } = usePageVisibility();

  const [twilioDevice, setTwilioDevice] = useState<Device | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<'deconnected' | 'connecting' | 'registered' | 'error'>('deconnected');
  const [activeCall, setActiveCall] = useState<any>(null);
  const [callState, setCallState] = useState<'idle' | 'ringing' | 'in-call'>('idle');
  const [outboundNumber, setOutboundNumber] = useState('');
  const [showSoftphonePanel, setShowSoftphonePanel] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const activeCallRef = useRef<any>(null);
  const [callerInfo, setCallerInfo] = useState<{ name: string; phone: string }>({ name: 'Client', phone: '' });

  const role = user?.role;
  const entrepriseId = role === "entreprise" ? user?.profile?.id : (user?.profile as Agent)?.entreprise_id;
  const agentId = user?.profile?.id;

  const resetCallState = () => {
    setActiveCall(null);
    activeCallRef.current = null;
    setCallState('idle');
    setShowSoftphonePanel(false);
    setIsMuted(false);
    
    try {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    } catch (error) {
      console.log('Erreur arrêt son:', error);
    }
  };

  useEffect(() => {
    let currentDevice: Device | null = null;

    async function initTwilio() {
      setDeviceStatus('connecting');
      const tokenData = await getTwilioToken();

      if (!tokenData || !tokenData.token) {
        setDeviceStatus('error');
        return;
      }

      try {
        currentDevice = new Device(tokenData.token, {
          logLevel: 1
        });

        currentDevice.on('registered', () => {
          setDeviceStatus('registered');
        });

        currentDevice.on('error', (err) => {
          console.error("Erreur Twilio Device :", err);
          setDeviceStatus('error');
        });

        currentDevice.on('incoming', (call) => {
          const from = call.parameters?.From || '';
          const formattedFrom = formatPhoneNumber(from);
          const clientName = call.parameters?.clientName || 'Client';
          
          setCallerInfo({
            name: clientName,
            phone: formattedFrom
          });

          activeCallRef.current = call;
          setActiveCall(call);
          setCallState('ringing');
          setShowSoftphonePanel(true);

          call.on('accept', () => {
            setCallState('in-call');
            closeNotifications();
          });

          call.on('disconnect', () => {
            console.log('Appel déconnecté par Twilio');
            resetCallState();
            closeNotifications();
          });

          call.on('cancel', () => {
            console.log('Appel annulé par l\'appelant');
            resetCallState();
            closeNotifications();
          });

          call.on('reject', () => {
            console.log('Appel rejeté');
            resetCallState();
            closeNotifications();
          });

          call.on('error', (error: any) => {
            console.error('Erreur appel:', error);
            resetCallState();
            closeNotifications();
          });
        });

        await currentDevice.register();
        setTwilioDevice(currentDevice);
      } catch (err) {
        console.error("Échec initialisation Twilio SDK :", err);
        setDeviceStatus('error');
      }
    }

    initTwilio();

    return () => {
      if (currentDevice) {
        currentDevice.destroy();
      }
    };
  }, [getTwilioToken]);

  useEffect(() => {
    if (callState === 'ringing' && !isVisible) {
      const clientName = callerInfo.name || 'Client';
      const clientPhone = callerInfo.phone || '';
      
      showNotification({
        title: '📞 Appel entrant',
        body: `${clientName}${clientPhone ? ` (${clientPhone})` : ''} vous appelle`,
        data: {
          clientName,
          clientPhone,
          callId: activeCall?.callSid || Date.now().toString(),
          timestamp: new Date().toISOString()
        }
      });
    }

    if (callState === 'ringing' && isVisible) {
      closeNotifications();
    }

  }, [callState, isVisible, showNotification, closeNotifications, callerInfo, activeCall]);

  const handleMakeCall = async (phoneNumber: string) => {
    if (!twilioDevice || deviceStatus !== 'registered') {
      alertError("Le softphone Twilio n'est pas prêt.");
      return;
    }

    if (!phoneNumber) {
      alertError("Veuillez entrer un numéro de téléphone valide.");
      return;
    }

    const formattedNumber = formatPhoneNumber(phoneNumber);
    
    if (!formattedNumber || formattedNumber.length < 8) {
      alertError("Le numéro de téléphone n'est pas valide après formatage.");
      return;
    }

    setOutboundNumber(formattedNumber);

    try {
      setCallState('ringing');
      setShowSoftphonePanel(true);

      if (!entrepriseId) {
        alertError("Entreprise non identifiée.");
        return;
      }

      const call = await twilioDevice.connect({
        params: {
          To: formattedNumber,
          entreprise_id: entrepriseId,
          agent_id: agentId || ""
        }
      });

      activeCallRef.current = call;
      setActiveCall(call);

      call.on('accept', () => {
        setCallState('in-call');
        alertSuccess(`Appel en cours vers ${formattedNumber}`);
      });

      call.on('disconnect', () => {
        console.log('Appel sortant déconnecté');
        resetCallState();
        closeNotifications();
        alertSuccess("Appel terminé");
      });

      call.on('error', (error) => {
        console.error("Erreur durant l'appel :", error);
        resetCallState();
        closeNotifications();
        alertError(`Erreur lors de l'appel : ${error.message || 'Erreur inconnue'}`);
      });

    } catch (error: any) {
      console.error("Échec de connexion de l'appel :", error);
      resetCallState();
      closeNotifications();
      alertError(`Impossible d'établir l'appel : ${error.message || 'Erreur inconnue'}`);
    }
  };

  const acceptCall = () => {
    if (activeCallRef.current) {
      activeCallRef.current.accept();
      setCallState('in-call');
      closeNotifications();
      alertSuccess("Appel accepté");
    }
  };

 // Remplacez uniquement la fonction hangUpCall dans useSoftphone.ts

const hangUpCall = async () => {
  console.log('🛑 Action de raccrochage demandée...');

  const currentCall = activeCallRef.current;

  if (currentCall) {
    try {
      // Obtenir l'état réel fourni par le SDK Twilio Voice (v2)
      const callStatus = typeof currentCall.status === 'function' 
        ? currentCall.status() 
        : currentCall.state;

      console.log('Statut de l\'appel au raccrochage :', callStatus);

      if (callStatus === 'pending' || callState === 'ringing') {
        // Rejeter l'invitation entrante (envoie un SIP 486 Busy à Twilio)
        currentCall.reject();
        console.log('✅ Appel entrant rejeté via .reject()');
      } else {
        // Couper un appel déjà en cours
        currentCall.disconnect();
        console.log('✅ Appel en cours déconnecté via .disconnect()');
      }
    } catch (error) {
      console.error('Erreur lors de la fermeture de l\'appel Twilio:', error);
    }
  } else {
    console.warn('Aucun appel actif trouvé dans activeCallRef');
  }

  // Nettoyage de l'interface React
  resetCallState();
  closeNotifications();
};

  const toggleMute = () => {
    if (activeCallRef.current) {
      const muted = activeCallRef.current.isMuted();
      activeCallRef.current.mute(!muted);
      setIsMuted(!muted);
    }
  };

  return {
    twilioDevice,
    deviceStatus,
    activeCall,
    callState,
    outboundNumber,
    isMuted,
    showSoftphonePanel,
    activeCallRef,
    setOutboundNumber,
    setShowSoftphonePanel,
    setIsMuted,
    handleMakeCall,
    acceptCall,
    hangUpCall,
    toggleMute,
  };
}